import { TriangleAlert } from 'lucide-react'

import type { StockOutSuggestion } from '@/lib/api/sales-order-api'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

const EPS = 1e-6

/** Một mặt hàng kho không đủ để xuất cho đơn. */
export interface StockOutShortage {
    kind: 'product' | 'material'
    id: string
    name: string
    unit: string
    needed: number
    available: number
    short: number
}

/**
 * Tính dòng thiếu từ gợi ý lô của popup xuất kho. Gợi ý của server đã vét mọi lô
 * còn bán được (sản phẩm: kho mặc định, bỏ kệ lỗi / lô ngừng; vật tư: lô còn
 * tồn) và trả phần thiếu thay vì lỗi, nên tổng phân bổ gợi ý = số còn lại khi
 * không đủ. Sản phẩm nhiều dòng cùng mã được gộp như server gộp khi gợi ý.
 * Thuần dữ liệu để POS (stockOutPreview, cùng shape) dùng lại được.
 */
export function computeStockOutShortages(
    suggestion: StockOutSuggestion | null | undefined,
): StockOutShortage[] {
    if (!suggestion) return []
    const out: StockOutShortage[] = []
    const products = new Map<string, StockOutShortage>()
    for (const it of suggestion.items) {
        const allocated = it.allocations.reduce((s, a) => s + a.quantity, 0)
        const prev = products.get(it.product_id)
        if (prev) {
            prev.needed += it.quantity
            prev.available += allocated
        } else {
            products.set(it.product_id, {
                kind: 'product',
                id: it.product_id,
                name: it.product_name,
                unit: it.unit,
                needed: it.quantity,
                available: allocated,
                short: 0,
            })
        }
    }
    for (const p of products.values()) {
        p.short = p.needed - p.available
        if (p.short > EPS) out.push(p)
    }
    for (const mt of suggestion.materials ?? []) {
        const available = mt.allocations.reduce((s, a) => s + a.quantity, 0)
        const short = mt.needed_qty - available
        if (short > EPS) {
            out.push({
                kind: 'material',
                id: mt.packaging_material_id,
                name: mt.material_name,
                unit: mt.unit,
                needed: mt.needed_qty,
                available,
                short,
            })
        }
    }
    return out
}

/** Khung cảnh báo kho thiếu hàng; không có dòng thiếu thì không vẽ gì. */
export function StockOutShortageAlert({
    shortages,
}: {
    shortages: StockOutShortage[]
}) {
    if (shortages.length === 0) return null
    return (
        <div
            role="alert"
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
        >
            <p className="flex items-center gap-2 font-medium">
                <TriangleAlert className="size-4 shrink-0" aria-hidden />
                {m.stock_out_shortage_title()}
            </p>
            <ul className="mt-2 space-y-1">
                {shortages.map((s) => {
                    const values = {
                        name: s.name,
                        needed: formatQty(s.needed),
                        available: formatQty(s.available),
                        short: formatQty(s.short),
                        unit: s.unit,
                    }
                    return (
                        <li key={`${s.kind}-${s.id}`}>
                            {s.kind === 'product'
                                ? m.stock_out_shortage_product_line(values)
                                : m.stock_out_shortage_material_line(values)}
                        </li>
                    )
                })}
            </ul>
            <p className="mt-2 text-xs">{m.stock_out_shortage_hint()}</p>
        </div>
    )
}
